import * as React from "react";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { MobXProviderContext, observer } from "mobx-react";
import { RouteStore } from "bernie-core";
import { ExternalLinkTabConfig, Navigation } from "src/stores/wizard/config";
import { useLocalization } from "@shared-ui/localization-context";
import { TabbedWizard } from "./TabbedWizard";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { getLobConfigFromState, getGTLabelTokenFromState } from "./ConfigHelpers/LobsConfig";
import { isExtLinkTab, getExternalLinkTabConfig, getExternalLinkTabURL } from "./ConfigHelpers/ExtLinkConfig";
import { wizardLobQueryParamKey, LOB_KEYS, LOB_ICONS_MAP } from "../constants";
import { withStores } from "stores";
import { FlexModuleModelStore } from "src/stores/FlexModuleModelStore";

/**
 * @property lobForms: a list of React elements representing each LOB form added in the flex manager
 */
export interface LobFormWithTabsProps {
  lobForms: React.ReactElement[];
  wizardFormClassNames?: string;
  tabGroupClassNames?: string;
  trackOnChangeTab?: (tabName: string, rfrrId?: string) => void;
  context?: ExtendedContextStore;
  flexModuleModelStore: FlexModuleModelStore;
  showTabsIcon?: boolean;
}

export const LobFormWithTabs = withStores("flexModuleModelStore")(
  observer((props: LobFormWithTabsProps) => {
    const {
      lobForms,
      wizardFormClassNames,
      tabGroupClassNames,
      trackOnChangeTab,
      context,
      flexModuleModelStore,
      showTabsIcon = true,
    } = props;
    const keyHelperWizard = new ItemKeyHelper("storefrontForms");
    const keyHelperNavigation = new ItemKeyHelper("storefrontWizardNavigation");
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const [isModuleModelStoreInitialized, setModuleModelStoreInitialized] = React.useState(false);
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };

    const { formatText } = useLocalization();

    /*
     * Temporary workaround to initialize wizard modules on server side rendering
     * from the flexModuleModelStore in the modelMap Map property.
     * We can remove this when the flex template renderer is refactored 100% in blossom.
     * Details of this implementation on this confluence page:
     * https://confluence.expedia.biz/display/POS/Flex+template+renderer+react+refactor+under+Wizard.
     */
    if (!isModuleModelStoreInitialized) {
      flexModuleModelStore.getModel("wizard-flight-pwa-1");
      flexModuleModelStore.getModel("wizard-hotel-pwa-v2-1");
      flexModuleModelStore.getModel("wizard-cruise-pwa-1");
      flexModuleModelStore.getModel("wizard-package-pwa-1");
      flexModuleModelStore.getModel("wizard-thirdPartyPackage-pwa-1");
      flexModuleModelStore.getModel("wizard-car-pwa-1");
      flexModuleModelStore.getModel("wizard-vr-pwa-1");
      flexModuleModelStore.getModel("wizard-lx-pwa-1");
      flexModuleModelStore.getModel("wizard-hotel-pwa-1");
      flexModuleModelStore.getModel("wizard-externalLinkTab-pwa-1");
      setModuleModelStoreInitialized(true);
    }

    //returns the localized string given the name of the flex component used
    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      const lobStateConfig = getLobConfigFromState(lobName, wizardState);
      if (lobStateConfig) {
        label = formatText(lobStateConfig?.navigation?.tabTitleToken);
      }
      if (LOB_KEYS.GT === lobName) {
        label = formatText(getGTLabelTokenFromState(wizardState));
      }

      return label;
    };

    //set the state to hold the default lob in the wizard store as the current value
    const [currentLOB, setCurrentLOB] = React.useState<string>(getLabel(wizardState.activeLob));

    /**
     * Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
     * CurrentLOB and defaultLOB set server side, but may be updated based on last search cookie value client side
     * To ensure up to date values on client side render, trigger an effect that does not have any dependent values to only run on first render
     */
    React.useEffect(() => {
      if (!routeStore.queryParams.pwaLob) {
        const { useDefaultLOB, defaultLob: pwaLob } = wizardState;
        if (useDefaultLOB) {
          wizardState.setUseDefaultLOB(false);
          setCurrentLOB(getLabel(pwaLob));
        }
      }
    }, []);

    //need to be able to map the localized version of the lob form name to the flex component name
    const LobFormNames: { [label: string]: string } = {};
    lobForms.map((lob: React.ReactElement) => {
      const { name, id } = lob?.props?.flexComponent;

      if (isExtLinkTab(name) && id) {
        LobFormNames[id] = name;
      } else {
        const formLabel = getLabel(name);
        LobFormNames[formLabel] = name;
      }
    });

    const getFormNameFromLabel = (label: string): string => {
      return LobFormNames[label];
    };

    const cleanupPwaLobQueryParam = () => {
      routeStore.updateSearch({ pwaLob: undefined });
    };

    const externalLinkTabs = lobForms.filter((lob) => lob?.key?.toString().includes(LOB_KEYS.EXT_LINK));

    //on selecting a tab, update the wizard state with the flex component name of the current tab, and update the react state with the localized label that will be used on the react component
    //update the query route with the new pwaLob and pwaSubLob
    const onTabSelect = (tabSelected: any) => {
      if (routeStore.queryParams.pwaLob) {
        cleanupPwaLobQueryParam();
      }

      const formName = getFormNameFromLabel(tabSelected);
      if (!isExtLinkTab(formName)) {
        if (trackOnChangeTab) {
          trackOnChangeTab(formName);
        }
        shouldUpdateLOBCookie(false);
        wizardState.setActiveLob(formName);
        setCurrentLOB(tabSelected);
        resetErrorsState();
      } else {
        const currentLob = externalLinkTabs.find((lob) => lob.key === tabSelected);
        const rfrrId = currentLob?.props.flexComponent.model.rfrrId;
        const externalLinkTabURL = getExternalLinkTabURL(formName, context, null, currentLob, wizardState);
        if (externalLinkTabURL) {
          window.open(externalLinkTabURL);
        }
        if (trackOnChangeTab) {
          trackOnChangeTab(formName, rfrrId);
        }
      }
    };

    const shouldUpdateLOBCookie = (shouldUpdate: boolean) => {
      wizardState.globalWizardState.updateShouldUpdateFromCookie(shouldUpdate);
    };

    const resetErrorsState = () => {
      wizardState.carWizardState.resetSubLobState();
      wizardState.carWizardState.resetSubLobTabs();
      wizardState.carWizardState.cleanErrorState();

      wizardState.lxWizardState.cleanErrorState();
      wizardState.hotelWizardState.cleanErrorState();
      wizardState.flightWizardState.cleanErrorState();
      wizardState.packageWizardState.cleanErrorState();
    };

    return (
      <>
        <UitkExperimentalButtonTabs
          onTabSelect={onTabSelect}
          selectedTab={currentLOB}
          tabsType={showTabsIcon ? UitkTabsType.NATURAL : UitkTabsType.EQUAL_WIDTH}
          className={`lobNavigationFormWithTabs ${tabGroupClassNames || ""}`}
          data-testid="lobNavigationFormWithTabs"
          tabAnchorType={showTabsIcon ? UitkTabAnchorType.ICON_AND_TEXT : UitkTabAnchorType.DEFAULT}
        >
          {lobForms.map((lob: React.ReactElement) => {
            const { name, id } = lob.props.flexComponent;
            const iconKey = showTabsIcon ? LOB_ICONS_MAP[name] : "";
            const label = getLabel(name);
            const defaultTabProps = {
              "data-testid": label,
              name: label,
              targetURI: `?${wizardLobQueryParamKey}=${name}`,
              id: name,
              iconKey,
            };

            if (isExtLinkTab(name)) {
              // wizard-externalLinkTab-pwa module only
              // renders only a tab without any content or form
              const externalLinkConfig: ExternalLinkTabConfig | null = getExternalLinkTabConfig(lob, wizardState);
              const { tabIcon, tabTitleToken } = externalLinkConfig?.navigation || {
                tabIcon: iconKey,
                tabTitleToken: null,
              };
              const extLinkTabProps = {
                ...defaultTabProps,
                displayName: tabTitleToken ? formatText(tabTitleToken) : undefined,
                name: id,
                targetURI: getExternalLinkTabURL(name, context, externalLinkConfig) || undefined,
                id,
                iconKey: tabIcon,
              };

              return <UitkTab {...extLinkTabProps} key={keyHelperWizard.next()} />;
            }

            return (
              <UitkTab {...defaultTabProps} key={keyHelperNavigation.next()}>
                {currentLOB === label && (
                  <div className={wizardFormClassNames || ""}>
                    <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
                  </div>
                )}
              </UitkTab>
            );
          })}
        </UitkExperimentalButtonTabs>
      </>
    );
  })
);
